'use client';

import { forwardRef } from 'react';
import { X } from '@phosphor-icons/react';
import { useLocale } from 'next-intl';
import { IconButton } from './icon-button';
import type { ButtonSize } from './button';

// Omit the HTML `color` attribute — it's typed `string` on every element and
// would widen the IconButton `color` prop (which expects ButtonColor) when
// spread. Also omit the native `size` so our `ButtonSize`-typed `size` wins.
interface OverlayCloseButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'color' | 'size'
  > {
  /** Default 'md' (36px). Use 'sm' (32px) for tighter overlay headers. */
  size?: ButtonSize;
}

/**
 * Consistent close (×) button for storefront overlays — dialogs, bottom
 * sheets, and side drawers.
 *
 * Composes the storefront `IconButton` with the neutral `soft` + `default`
 * variant + a slightly muted icon, giving a soft circular pill at rest and a
 * clear hover step-up. Theme-agnostic.
 *
 * Use it two ways:
 *   1. Plain button:           `<OverlayCloseButton onClick={onClose} />`
 *   2. base-ui Close render:   `<SheetClose render={<OverlayCloseButton />} />`
 *      (base-ui merges its own onClick/refs onto the rendered button.)
 */
export const OverlayCloseButton = forwardRef<
  HTMLButtonElement,
  OverlayCloseButtonProps
>(function OverlayCloseButton({ size = 'md', className, ...props }, ref) {
  const locale = useLocale();
  const label = locale === 'ar' ? 'إغلاق' : 'Close';

  return (
    <IconButton
      ref={ref}
      type="button"
      aria-label={label}
      variant="soft"
      color="default"
      size={size}
      // `shape` defaults to 'rounded' on IconButton — circle implied.
      className={className}
      icon={
        <>
          <X weight="bold" />
          <span className="sr-only">{label}</span>
        </>
      }
      {...props}
    />
  );
});
